import React, { Component } from 'react'
import { withRouter } from 'react-router'
import { inject, observer } from 'mobx-react'
import { toJS, extendObservable, action } from 'mobx'
import PropTypes from 'prop-types'
import { Formik, Form, Field } from 'formik'
import pick from 'lodash/pick'
import moment from 'moment'
import Tooltip from '@components/Tooltip/Tooltip'
import InfoIcon from '@components/Icon/Info'

import { officeApi, projectApi } from '@atheneum/api'
import { getSubIndustryOptions, convertStaticDataToOptions } from '@atheneum/api/dist/lib'
import { getValidationSchema } from './ProjectFormValidationSchema'

import Button from '@components/Button'
import {
  Dropdown,
  Autocomplete,
  TagsInput
} from '@components/Form/Autocomplete'
import RadioGroup from '@components/Form/RadioGroup'
import Checkbox from '@components/Form/Checkbox'
import DatePicker from '@components/Form/DatePicker'
import Input from '@components/Form/Input'
import RichTextEditor from '@components/Form/RichTextEditor'
import SegmentFields from '@components/Form/SegmentFields'
import TextArea from '@components/Form/TextArea'
import FormError from '@components/FormError'
import FormLoader from '@components/FormLoader'
import Spinner from '@components/spinner'

import ProjectFormStore from './projectForm.store'

import {
  defaultExpertBrief,
  defaultScreeningQuestion
} from '../SegmentForm/formDefaults'

class ProjectForm extends Component {
  static propTypes = {
    contact: PropTypes.object,
    staticData: PropTypes.object,
    userStore: PropTypes.object,
    sidebarStore: PropTypes.object,
    modalStore: PropTypes.object,
    projectStore: PropTypes.object,
    growlStore: PropTypes.object,
    project: PropTypes.object,
    singleContact: PropTypes.object,
    history: PropTypes.object
  }

  constructor(props) {
    super(props)

    const { staticData, userStore, project, singleContact, contact } = props

    extendObservable(this, {
      projectFormStore: new ProjectFormStore(
        staticData,
        userStore,
        project,
        singleContact || contact
      )
    })

    this.state = {
      error: null,
      clientApplicableContracts: []
    }
  }

  handleOfficeAutocomplete = async input => {
    if (!input) return []

    const { project } = this.projectFormStore
    const projectId = project && !project.capiProjectId ? project.id : undefined

    return this.projectFormStore
      .getOfficeSuggestions(input, projectId)
      .then(offices => {
        return offices
      })
      .catch(err => {
        console.error(err)
      })
  }

  onOfficeChange = (office, form) => {
    form.setFieldValue('office', office, false)
    form.setFieldTouched('office', true, true)

    form.setFieldValue('clients', [], false)

    if (office) {
      form.setFieldValue('officeId', office.id, false)

      return officeApi.getOffice(office.id).then(({ account }) => {
        this.setAccount(account, form)
        this.getClientApplicableContracts(account.id)
      })
    } else {
      this.setAccount(null, form)
      this.getClientApplicableContracts(null)
    }
  }

  setAccount = (account, form) => {    
    const { project } = this.projectFormStore

    if (!account) {
      form.setFieldValue('office.account', null, false)
      form.setFieldValue('office.accountId', null, false)
      this.projectFormStore.parentAccountId = project?.platformClientId || null
      return
    }

    const changedAccount = {
      id: account.id,
      companyName: account.companyName,
      parentAccountId: account.parentAccountId
    }

    form.setFieldValue('office.account', changedAccount, false)
    form.setFieldValue('office.accountId', changedAccount.id, false)
    this.projectFormStore.parentAccountId = changedAccount.parentAccountId
  }

  clientOfficeSidebarLink = form => {
    const officeId = form.values.officeId
    this.props.sidebarStore.openSidebar({
      id: officeId,
      template: 'client-office'
    })
  }

  clientAccountSidebarLink = form => {
    if (form.values.office) {
      const accountId = form.values.office.accountId
      this.props.sidebarStore.openSidebar({
        id: accountId,
        template: 'client-account'
      })
    }
  }

  getClientApplicableContracts = action(id => {
    if (!id) {
      return this.setState({ clientApplicableContracts: [] })
    }
    this.projectFormStore.getAccountContracts(id).then(contracts => {
      const errMessage =
        contracts.length < 1
          ? 'There is no active contract in place for this account. Please contact contractrequest@atheneum-partners.com'
          : null

      this.error = errMessage
      this.setState({ clientApplicableContracts: contracts })
    })
  })

  renderClientAccountApplicableContracts = () => {
    const { clientApplicableContracts } = this.state
    if (
      !clientApplicableContracts ||
      toJS(clientApplicableContracts).length < 1
    ) {
      return
    }
    return (
      <div className='expert-form__input-group'>
        <label className='expert-form__label'>Applicable contracts</label>
        <div className='expert-form__input-wrapper'>
          {clientApplicableContracts.map(contract => {
            return (
              <span
                key={contract.id}
                className='expert-form__contract'
                onClick={() => this.clientContractSidebarLink(contract.id)}
              >
                {contract.contractReference}
              </span>
            )
          })}
        </div>
      </div>
    )
  }

  clientContractSidebarLink = contractId => {
    this.props.sidebarStore.openSidebar({
      id: contractId,
      template: 'contract'
    })
  }

  renderIndustrySubindustryItem = industryOrSubindustry => {
    const isIndustry = industryOrSubindustry.type === 'industry'
    if (isIndustry) {
      return (
        <div>
          <b>{industryOrSubindustry.name}</b>
        </div>
      )
    }

    return (
      <div className='autocomplete__item--suboption'>
        {industryOrSubindustry.name}
      </div>
    )
  }

  handleSubIndustryChange = (value, form) => {
    if (value) {
      form.setFieldValue('subIndustryId', value.id, false)
      form.setFieldValue('subIndustry', value, false)
      form.setFieldValue('industryId', value.industryId, false)
    } else {
      form.setFieldValue('subIndustryId', '', false)
      form.setFieldValue('subIndustry', {}, false)
      form.setFieldValue('industryId', '', false)
    }
  }

  submitForm = (projectData, actions) => {
    const { modalStore, history } = this.props
    const { project } = this.projectFormStore
    this.saveProject(projectData)
      .then(savedProject => {
        (project && !project.capiProjectId)
          ? this.props.projectStore.get(savedProject.id)
          : history.push('/project/' + savedProject.id)
        modalStore.close()
      })
      .catch(({ message }) => {
        this.setState({ error: message })
        actions.setSubmitting(false)
      })
  }

  saveProject = projectData => {
    const { project } = this.projectFormStore

    const employees = projectData.employees.map(employee => {
      employee.employeeToProject = {
        units: 0,
        employeeId: employee.id
      }
      return employee
    })

    const startDate = moment().format('YYYY-MM-DD')

    const savedData = {
      ...projectData,
      employees,
      startDate
    }


    if (project && !project.capiProjectId) {
      return projectApi.update(project, savedData).then(res => {
          this.props.growlStore.showSuccess('Project updated successfully')
          return res
        })
        .catch(err => {
          this.props.growlStore.showError(err.message)
        })
    } else {
      return projectApi.create(savedData)
        .then(savedProject => {
          if(project?.capiProjectId) {
            return projectApi.updateCapiProject(project.capiProjectId, savedProject.id)
          } else {
            return savedProject
          }
        })
        .then(savedProject => {
          if(project?.capiProjectId) {
            this.props.clientProjectsStore.loadClientProjects()
          }
          this.props.growlStore.showSuccess('Project created successfully')
          return savedProject
        })
        .catch(err => {
          if (err.message === 'NO_BOARD') return
          this.props.growlStore.showError(err.message)
        })
    }
  }

  renderItemName = item => item && item.name

  renderFullName = item => item && item.fullName

  handleClientAutocomplete = async input => {
    return this.projectFormStore.getClientOptions(input)
  }

  onClientChange = (clients, form) => {
    const { project } = this.projectFormStore
    const client = clients[0]

    if ((!this.projectFormStore.parentAccountId || project?.platformClientId) && client) {
      this.onOfficeChange(
        {
          id: client.office.id,
          officeName: client.office.officeName
        },
        form
      )
    }

    form.setFieldValue('clients', clients, false)
  }

  render() {
    const { staticData, modalStore, contact, projectStore } = this.props
    const {
      loading,
      project,
      employees,
      bdManagerEmployees
    } = this.projectFormStore

    if (loading) return <Spinner />

    return (
      <div>
        <Formik
          enableReinitialize
          initialValues={initializeForm({ project, staticData, contact })}
          validationSchema={getValidationSchema(defaultExpertBrief)}
          onSubmit={this.submitForm}
          render={form => (
            <div className='expert-form'>
              <FormLoader loading={form.isSubmitting} />
              <h1 className='expert-form__title'>
                {project
                  ? `Edit project ${project.projectName}`
                  : 'Create new project'}
              </h1>
              <FormError error={this.state.error} />
              <Form onSubmit={form.handleSubmit}>
                <div className='expert-form__section'>
                  <h2 className='expert-form__subtitle'>Client details</h2>
                  <Field
                    data-cy='client-office-autocomplete'
                    name='office'
                    label='Client office *'
                    placeholder=''
                    itemToString={item => item && item.officeName}
                    loadOptions={this.handleOfficeAutocomplete}
                    onChange={value => this.onOfficeChange(value, form)}
                    linkLabel='Details'
                    linkCallback={() => this.clientOfficeSidebarLink(form)}
                    component={Autocomplete}
                  />
                  <Field
                    name='office.account.companyName'
                    label='Client account'
                    component={Input}
                    linkLabel='Details'
                    linkCallback={() => this.clientAccountSidebarLink(form)}
                    disabled
                  />
                  {this.renderClientAccountApplicableContracts()}
                  <Field
                    data-cy='client-contact-multiple'
                    name='clients'
                    label='Client contact *'
                    itemToString={item => item && item.fullName}
                    component={TagsInput}
                    loadOptions={this.handleClientAutocomplete}
                    onChange={value => this.onClientChange(value, form)}
                    debounce={300}
                  />
                  <Field
                    short
                    name='caseCode'
                    label='Case code'
                    onChange={e =>
                      form.setFieldValue(
                        'caseCode',
                        e.target.value.toUpperCase(),
                        false
                      )
                    }
                    disabled={!!projectStore?.capiDetails}
                    component={Input}
                  />
                  <Field
                    name='clientAvailabilities'
                    label='Client availability'
                    component={TextArea}
                  />
                  <Field
                    name='languages'
                    label='Preferred client languages'
                    itemToString={this.renderItemName}
                    options={toJS(
                      this.projectFormStore.preferredClientLanguages
                    )}
                    component={TagsInput}
                  />
                  {project && project.office?.account &&
                    <Field
                      className='m-l-0'
                      name='complianceRequired'
                      disabled={!project.office.account.externalComplianceRequired}
                      label='External Compliance verification'
                      tooltip='Check Client Account settings to set or override this setting'
                      component={Checkbox}
                    />
                  }
                </div>
                <div className='expert-form__section'>
                  <h2 className='expert-form__subtitle'>Project details</h2>
                  <Field
                    name='projectName'
                    label='Project name *'
                    component={Input}
                  />
                  <Field
                    name='projectCategoryId'
                    label='Project category'
                    itemToString={i => i ? i.name : ''}
                    component={Dropdown}
                    options={staticData.projectCategories.sort((a, b) => a.id -b.id)}
                  />
                  <Field
                    name='employees'
                    data-cy='atheneum-contact-multiple'
                    label={
                      <div>
                        Atheneum contact *{' '}
                        <Tooltip tooltip='The Atheneum PM for this project must be added as first Atheneum Contact, so that the project is added to the appropriate Project Board in Monday. Additional Atheneum Contacts (e.g. from BD or other offices) can still be added as 2nd, 3rd, etc.'>
                          <InfoIcon />
                        </Tooltip>
                      </div>
                    }
                    itemToString={this.renderFullName}
                    options={employees}
                    component={TagsInput}
                  />
                  <Field
                    name='bdContactId'
                    label='BD Manager contact'
                    itemToString={this.renderFullName}
                    options={bdManagerEmployees}
                    onChange={item => {
                      form.setFieldValue(
                        'bdContactId',
                        item ? item.id : null,
                        false
                      )
                    }}
                    valueIsId
                    component={Autocomplete}
                  />
                  <Field
                    showResultsOnEmptyInput
                    data-cy='industry-subindustry-autocomplete'
                    name='subIndustry'
                    label='Industry / subindustry *'
                    placeholder='Select industry/subindustry'
                    itemToString={this.renderItemName}
                    renderItem={this.renderIndustrySubindustryItem}
                    options={getSubIndustryOptions(staticData)}
                    onChange={value =>
                      this.handleSubIndustryChange(value, form)
                    }
                    component={Autocomplete}
                  />
                  <Field
                    name='projectTypeId'
                    label='Project type'
                    options={convertStaticDataToOptions(
                      staticData.projectTypes
                    )}
                    component={RadioGroup}
                  />
                  <Field
                    className='m-l-0'
                    name='inProposal'
                    label='Preliminary list / Proposal'
                    component={Checkbox}
                  />
                  <Field
                    className='m-l-0'
                    name='sendComplianceReminders'
                    label='Send compliance reminders'
                    tooltip='If an expert on the project has not agreed to their compliance form by the time an interview is scheduled, they will get a reminder email immediately, followed by two more, 48 and 24 hours before the scheduled interview. In addition, employees assigned to the EPL will also receive reminders 8 work-hours prior to the scheduled interview. Any successive emails will not be sent once the compliance is signed. Re-scheduling interviews will also result in any reminders pertaining to the original interview date to be ignored. The first expert reminder will still be sent, however.'
                    component={Checkbox}
                  />
                  <Field
                    className='m-l-0'
                    name='hideExpertExperience'
                    label='Hide expert employer and position from client'
                    tooltip='Hides experts position and company from client view and export'
                    component={Checkbox}
                  />
                  <Field
                    data-cy='background-richtext'
                    name='background'
                    label='Background *'
                    placeholder=''
                    toolbarRight
                    component={RichTextEditor}
                  />
                  <Field
                    name='blacklistedCompanies'
                    label='Blacklisted companies'
                    placeholder=''
                    toolbarRight
                    component={RichTextEditor}
                  />
                  <Field
                    name='interviewTarget'
                    label='Target no. of Interviews *'
                    component={Input}
                  />
                  <Field
                    short
                    dateOnly
                    name='applyBeforeDate'
                    label='Apply before date'
                    minDate={moment().format('YYYY-MM-DD')}
                    onChange={value => {
                      if (!value) {
                        form.setFieldValue('applyBeforeDate', null, false)
                      } else {
                        form.setFieldValue('applyBeforeDate', value, false)
                        form.setFieldTouched('applyBeforeDate', true)
                      }
                    }}
                    component={DatePicker}
                  />
                  <Field
                    name='invoicingInstructions'
                    label='Invoicing instructions'
                    placeholder=''
                    toolbarRight
                    component={RichTextEditor}
                  />
                  <Field
                    name='additionalClientArangements'
                    label='Additional client agreements'
                    itemToString={i => i.id}
                    component={Dropdown}
                    options={[{ id: 'None' }, { id: 'Unlimited Calls' }]}
                  />
                </div>
                {(!project || project.capiProjectId) && (
                  <div className='expert-form__section'>
                    <h2 className='expert-form__subtitle'>Segments</h2>
                    <SegmentFields
                      form={form}
                      name='segments'
                      labelName='Segment title *'
                      labelNumberOfExpert='No. of experts required *'
                      labelExpertBrief='Expert brief *'
                      defaultExpertBrief={defaultExpertBrief}
                      defaultScreeningQuestion={defaultScreeningQuestion}
                    />
                  </div>
                )}
                <p className='expert-form__label'>
                  Fields marked with * are required
                </p>
                <div className='expert-form__section p-b-0'>
                  <Button
                    primary
                    submit
                    onClick={this.onSubmit}
                    disabled={form.isSubmitting}
                  >
                    Save
                  </Button>
                  <Button onClick={modalStore.close}>Cancel</Button>
                </div>
              </Form>
            </div>
          )}
        />
      </div>
    )
  }
}

ProjectForm.defaultProps = {
  hideButtons: true,
  showCloseIcon: true,
  shouldCloseOnOverlayClick: false
}

function initializeForm({ project, staticData, contact }) {
  let clients = []
  let officeId = ''
  let office = {
    id: '',
    officeName: '',
    accountId: '',
    account: {
      id: '',
      companyName: '',
      parentAccountId: ''
    }
  }
  if (contact) {
    clients = [
      {
        id: contact.id,
        fullName: contact.fullName
      }
    ]
    officeId = contact.client.officeId
    office = contact.client.office
    office.account = contact.client.account
  }

  let projectData = {
      officeId: officeId,
      office: office,
      clients: clients,
      caseCode: '',
      clientAvailabilities: '',
      languages: [],
      projectName: '',
      employees: [],
      bdContactId: null,
      projectTypeId: staticData.CONST.PROJECT_TYPES.EXPERT_SESSIONS,
      inProposal: false,
      sendComplianceReminders: false,
      subIndustryId: '',
      subIndustry: {},
      industryId: '',
      tags: [],
      additionalClientArangements: 'None',
      background: '',
      blacklistedCompanies: '',
      interviewTarget: '',
      invoicingInstructions: '',
      segments: [
        {
          name: '',
          numberOfExpert: '',
          expertBrief: defaultExpertBrief,
          screeningQuestions: [
            {
              question: defaultScreeningQuestion
            }
          ]
        }
      ],
      projectStatusId: staticData.CONST.PROJECT_STATUSES.OPEN,
      projectRequiredResourcesId:
        staticData.CONST.PROJECT_REQUIRED_RESOURCES.MEDIUM,
      complianceRequired: false,
    }

  if (project) {
    const projectProperties = pick(toJS(project), [
      'id',
      'officeId',
      'office.id',
      'office.officeName',
      'office.accountId',
      'office.account.id',
      'office.account.companyName',
      'office.account.parentAccountId',
      'additionalClientArangements',
      'clients',
      'caseCode',
      'clientAvailabilities',
      'languages',
      'projectName',
      'employees',
      'bdContactId',
      'projectTypeId',
      'projectCategoryId',
      'inProposal',
      'sendComplianceReminders',
      'hideExpertExperience',
      'subIndustryId',
      'industryId',
      'tags',
      'background',
      'blacklistedCompanies',
      'interviewTarget',
      'projectStatusId',
      'projectRequiredResourcesId',
      'invoicingInstructions',
      'applyBeforeDate',
      'complianceRequired'
    ])

    projectData = {
      ...projectData,
      ...projectProperties,
      subIndustry: {
        id: project.subIndustryId,
        name: staticData.names.subIndustries[project.subIndustryId]
      },
    }

    if(project.capiProjectId) {
      projectData.segments = toJS(project.segments)
    }
  } 

  return projectData
}

export default withRouter(
  inject(
    'staticData',
    'userStore',
    'sidebarStore',
    'modalStore',
    'growlStore',
  )(observer(ProjectForm))
)
